import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CardContent, Typography, CircularProgress, Alert, TextField, InputAdornment, FormControlLabel, Switch, Stack } from '@mui/material';
import { Search, PriorityHigh } from '@mui/icons-material';
import { useGetTicketsPendingCustomerQuery } from '../../../services/TicketService';
import { PageContainer, PageCard, SectionHeader, SectionPaper } from '../../common/components/styled-ui-elements';
import WorklistGrid from '../components/TicketList/WorklistGrid';
import { useMsal, useAccount } from '@azure/msal-react';
import { BackToDashboardButton, FloatingActionButton } from '../../common/components/navigation';

const TicketsPendingResponse: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const customerName = searchParams.get('customerName') || account?.name || '';
  const [searchText, setSearchText] = useState('');
  
  const { data: tickets, error, isLoading } = useGetTicketsPendingCustomerQuery(
    { customerName },
    { skip: !customerName }
  );

  if (isLoading) {
    return (
      <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
        <Alert severity="error">
          Failed to load pending tickets. Please try again.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #f59e0b',
            p: 2.5
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <SectionHeader 
                  icon={<PriorityHigh sx={{ mr: 1, color: '#f59e0b' }} />}
                  title="Pending My Response"
                />
                <Typography 
                  variant="body2" 
                  sx={{ color: '#64748b', ml: 4 }}
                >
                  Tickets waiting for your response - {customerName}
                </Typography>
              </Box>
              <BackToDashboardButton />
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Alert for pending tickets */}
            {tickets && tickets.length > 0 && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  '& .MuiAlert-icon': { color: '#f59e0b' }
                }}
              >
                <Typography variant="body2">
                  You have {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} waiting for your response. 
                  Take action to avoid delays in resolution.
                </Typography>
              </Alert>
            )}

            {/* Search Section */}
            <SectionPaper sx={{ mb: 3 }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search pending tickets by title, description, category, or assignee..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#f9fafb',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                      },
                    },
                  }}
                />
              </Stack>
            </SectionPaper>
            
            {tickets && tickets.length === 0 ? (
              <Alert 
                severity="success" 
                sx={{ 
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  '& .MuiAlert-icon': { color: '#10b981' }
                }}
              >
                <Typography variant="body2">
                  Great! You don't have any tickets waiting for your response.
                </Typography>
              </Alert>
            ) : (
              <WorklistGrid 
                tickets={tickets || []} 
                searchText={searchText} 
                showClosedTickets={false}
              />
            )}
          </CardContent>
        </PageCard>
      </Box>
      
      {/* Floating Action Button */}
      <FloatingActionButton 
        label="Create Ticket" 
        navigateTo="/tickets/create" 
        ariaLabel="Create new ticket"
      />
    </PageContainer>
  );
};

export default TicketsPendingResponse;